import { supabase } from '../lib/supabase';
import type { Database } from '../types/database';
import type { RespaldoMovimiento } from '../types/comprobante';
import type { OwnerFondoCampana } from '../types/fondosCampana';
import { lanzarErrorOperacionCampana, ResultadoCampanaInciertoError } from '../utils/fondosCampanaRpc';

type Movimiento = Database['public']['Tables']['movimientos']['Row'];
export interface AjusteFondoCampana {
  readonly id: string;
  readonly campanaId: string;
  readonly owner: OwnerFondoCampana;
  readonly tipo: 'ingreso' | 'egreso';
  readonly monto: number;
  readonly fecha: string;
  readonly motivo: string;
  readonly actorId: string;
  /** Un gasto o ajuste del fondo es un movimiento registrado a mano: sigue la regla general. */
  readonly respaldo: RespaldoMovimiento;
}

export interface CampanaGastoFinal {
  readonly id: string;
  readonly nombre: string;
}

function verificarOperacion(mov: Movimiento, p: AjusteFondoCampana): Movimiento {
  const mismaEntidad = mov.lumisial_id === (p.owner.tipo === 'lumisial' ? p.owner.id : null)
    && mov.diocesis_id === (p.owner.tipo === 'diocesis' ? p.owner.id : null)
    && mov.provincia_eclesiastica_id === (p.owner.tipo === 'provincia' ? p.owner.id : null);
  if (!mismaEntidad || mov.origen_tipo !== 'campaña' || mov.origen_id !== p.campanaId
    || mov.tipo !== p.tipo || mov.monto !== p.monto || mov.fecha !== p.fecha
    || mov.descripcion !== p.motivo || mov.registrado_por_usuario_id !== p.actorId
    || mov.afecta_balance || mov.aporte_campana_id || mov.envio_campana_id) {
    throw new ResultadoCampanaInciertoError();
  }
  return mov;
}

async function buscar(id: string): Promise<Movimiento | null> {
  const { data, error } = await supabase.from('movimientos').select('*').eq('id', id).maybeSingle();
  if (error) lanzarErrorOperacionCampana(error);
  return data;
}

export const movimientoFondoCampanaService = {
  async getCampanasDestinoFinal(owner: OwnerFondoCampana): Promise<CampanaGastoFinal[]> {
    if (owner.tipo === 'lumisial') {
      const { data, error } = await supabase.from('campañas').select('id,nombre')
        .eq('lumisial_id', owner.id).eq('nivel_origen', 'local').order('nombre');
      if (error) throw error;
      return data ?? [];
    }
    let query = supabase.from('campanas_master').select('id,nombre').order('nombre');
    if (owner.tipo === 'diocesis') query = query.eq('nivel_origen', 'diocesano').eq('diocesis_id', owner.id);
    else query = query.or(`and(nivel_origen.eq.nacional,provincia_eclesiastica_id.eq.${owner.id}),nivel_origen.eq.global`);
    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  },

  /**
   * ID fijo: un reintento consulta el registro o reproduce el mismo INSERT. No compara el respaldo: el id
   * ya identifica la operación, y un reintento después de adjuntar no debe fallar por eso.
   */
  async registrar(p: AjusteFondoCampana): Promise<Movimiento> {
    const previo = await buscar(p.id).catch(lanzarErrorOperacionCampana);
    if (previo) return verificarOperacion(previo, p);
    const { data, error } = await supabase.from('movimientos').insert({
      id: p.id, origen_tipo: 'campaña', origen_id: p.campanaId,
      lumisial_id: p.owner.tipo === 'lumisial' ? p.owner.id : null,
      diocesis_id: p.owner.tipo === 'diocesis' ? p.owner.id : null,
      provincia_eclesiastica_id: p.owner.tipo === 'provincia' ? p.owner.id : null,
      tipo: p.tipo, monto: p.monto, fecha: p.fecha, descripcion: p.motivo,
      registrado_por_usuario_id: p.actorId, afecta_balance: false,
      anulado: false, requiere_reembolso: false, reembolsado: false,
      ...p.respaldo,
    }).select('*').single().then(undefined, lanzarErrorOperacionCampana);
    if (error?.code === '23505') {
      const existente = await buscar(p.id).catch(lanzarErrorOperacionCampana);
      if (existente) return verificarOperacion(existente, p);
      throw new ResultadoCampanaInciertoError();
    }
    if (error) lanzarErrorOperacionCampana(error);
    if (!data) throw new ResultadoCampanaInciertoError();
    return verificarOperacion(data, p);
  },
};
